import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { format, isSameDay, isToday, type Locale } from "date-fns";
import { formatTime } from "@/lib/dates";
import type { Identifier } from "ra-core";
import { useGetMany, useTranslate } from "ra-core";
import { Link } from "react-router";

import type { Contact, Sale } from "../types";
import { dayKey, timelineHours } from "./mobileModel";
import { SOURCE_BAR, type AgendaEntry } from "./useAgendaEntries";
import { TaskKindBadge } from "../tasks/TaskKindBadge";
import { cleanNamePart, formatPersonName } from "../misc/personName";

/** Seven tappable days with a dot for days that have something on them. */
export const AgendaDayStrip = ({
  days,
  selected,
  busyKeys,
  dfLocale,
  onSelect,
}: {
  days: Date[];
  selected: Date | null;
  busyKeys: ReadonlySet<string>;
  dfLocale: Locale;
  onSelect: (day: Date) => void;
}) => (
  <div className="mb-6 flex gap-1">
    {days.map((day) => {
      const key = dayKey(day);
      const active = selected != null && isSameDay(day, selected);
      return (
        <button
          key={key}
          type="button"
          onClick={() => onSelect(day)}
          aria-pressed={active}
          aria-label={format(day, "PPPP", { locale: dfLocale })}
          className={cn(
            "flex min-w-0 flex-1 flex-col items-center rounded-2xl py-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring",
            active
              ? "bg-card text-foreground shadow-card ring-1 ring-border"
              : "text-muted-foreground",
          )}
        >
          <span className="uppercase tracking-wide">
            {format(day, "EEE", { locale: dfLocale }).replace(".", "")}
          </span>
          <span
            className={cn(
              "text-base font-semibold",
              isToday(day) && !active && "text-brand dark:text-foreground",
            )}
          >
            {format(day, "d")}
          </span>
          <span
            className={cn(
              "mt-1 size-1 rounded-full",
              busyKeys.has(key)
                ? "bg-brand dark:bg-foreground"
                : "bg-transparent",
            )}
          />
        </button>
      );
    })}
  </div>
);

/** All-day items first, then an hourly timeline (8–20 plus busy hours). */
export const AgendaDayTimeline = ({
  day,
  entries,
  locale,
}: {
  day: Date;
  entries: AgendaEntry[];
  locale: string;
}) => {
  const translate = useTranslate();
  const allDay = entries.filter((entry) => entry.allDay);
  const timed = entries.filter((entry) => !entry.allDay);
  const hours = timelineHours(
    timed.map((entry) => entry.start),
    day,
  );
  const contactIds = uniqueIds(entries.map((entry) => entry.contact_id));
  const salesIds = uniqueIds(entries.map((entry) => entry.assignee_id));
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const time = { format: (date: Date) => formatTime(date, locale) };

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.mobile.no_events")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {allDay.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {translate("crm.mobile.all_day")}
          </h2>
          {allDay.map((entry) => (
            <AgendaCard
              key={entry.id}
              entry={entry}
              contacts={contacts}
              sales={sales}
              when={null}
            />
          ))}
        </section>
      ) : null}
      <div className="flex flex-col">
        {hours.map((hour) => {
          const items = timed.filter(
            (entry) => entry.start.getHours() === hour,
          );
          return (
            <div
              key={hour}
              className="grid grid-cols-[3.25rem_1fr] gap-2 py-1.5"
            >
              <p className="pt-3 text-xs text-muted-foreground">
                {time.format(new Date(2026, 0, 1, hour))}
              </p>
              <div className="flex min-h-12 flex-col gap-2 border-t border-border/70 pt-2">
                {items.map((entry) => (
                  <AgendaCard
                    key={entry.id}
                    entry={entry}
                    contacts={contacts}
                    sales={sales}
                    when={
                      entry.end
                        ? `${time.format(entry.start)} – ${time.format(entry.end)}`
                        : time.format(entry.start)
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const uniqueIds = (ids: (Identifier | null | undefined)[]): Identifier[] => [
  ...new Set(ids.filter((id): id is Identifier => id != null)),
];

const AgendaCard = ({
  entry,
  contacts,
  sales,
  when,
}: {
  entry: AgendaEntry;
  contacts?: Contact[];
  sales?: Sale[];
  when: string | null;
}) => {
  const translate = useTranslate();
  const contact = contacts?.find((item) => item.id === entry.contact_id);
  const sale = sales?.find((item) => item.id === entry.assignee_id);
  const people: { id: string; src?: string; label: string }[] = [];
  if (contact) {
    people.push({
      id: `c-${contact.id}`,
      src: contact.avatar?.src,
      label: cleanNamePart(contact.first_name) || formatPersonName(contact),
    });
  }
  if (sale) {
    people.push({
      id: `s-${sale.id}`,
      src: sale.avatar?.src,
      label: cleanNamePart(sale.first_name) || formatPersonName(sale),
    });
  }
  const status = entry.done
    ? translate("crm.mobile.status_done")
    : entry.start.getTime() > Date.now()
      ? translate("crm.mobile.status_upcoming")
      : translate("crm.mobile.status_in_progress");
  const body = (
    <>
      <span
        className={cn(
          "w-1 self-stretch rounded-full",
          SOURCE_BAR[entry.source] ?? "bg-chart-1",
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="line-clamp-3 font-medium">{entry.title}</span>
        <span className="mt-1 flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            {entry.internal ? <TaskKindBadge /> : null}
            <span className="truncate">{status}</span>
          </span>
          {people.length > 0 ? (
            <span className="flex -space-x-2">
              {people.map((person) => (
                <Avatar key={person.id} className="size-6 ring-2 ring-card">
                  <AvatarImage src={person.src} alt="" />
                  <AvatarFallback className="text-[10px]">
                    {person.label.slice(0, 1)}
                  </AvatarFallback>
                </Avatar>
              ))}
            </span>
          ) : null}
        </span>
      </span>
      {when ? (
        <span className="max-w-24 shrink-0 text-right text-xs text-muted-foreground">
          {when}
        </span>
      ) : null}
    </>
  );
  const className =
    "flex items-stretch gap-3 rounded-4xl bg-card p-3.5 shadow-card ring-1 ring-border";
  if (!entry.href) return <article className={className}>{body}</article>;
  return (
    <Link to={entry.href} className={className}>
      {body}
    </Link>
  );
};
